import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { WaterlilyClient } from '@/api/waterlily';
import { postSendConsentNotificationEmailRequest, previewClientUpdates } from '@client/model';

export interface ConsentEmailPromptTarget {
  client: Client;
  isPrimaryClient: boolean;
  requestType: SendConsentNotificationEmailRequestType;
  consentType: 'start' | 'submit';
  clientPolicyApplicationId: string;
  onSent: () => void;
}

export function useConsentEmailPrompt() {
  const dispatch = useDispatch();
  const [target, setTarget] = useState<ConsentEmailPromptTarget | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openPrompt = useCallback((next: ConsentEmailPromptTarget) => {
    setTarget(next);
  }, []);

  const closePrompt = useCallback(() => {
    setTarget(null);
  }, []);

  const submitEmail = useCallback(
    async (email: string) => {
      if (!target) return;

      setIsSubmitting(true);
      try {
        const updatedClient = { ...target.client, clientEmail: email };
        await WaterlilyClient.put(`/client/${target.client.clientId}`, updatedClient);

        if (target.isPrimaryClient) {
          dispatch(previewClientUpdates(updatedClient));
        }

        dispatch(
          postSendConsentNotificationEmailRequest({
            clientPolicyApplicationId: target.clientPolicyApplicationId,
            requestType: target.requestType,
            consentType: target.consentType,
          }),
        );

        target.onSent();
        setTarget(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, target],
  );

  return {
    isOpen: target !== null,
    clientName: target?.client.clientFullName,
    isSubmitting,
    openPrompt,
    closePrompt,
    submitEmail,
  };
}
